import React from 'react';
import { FileText, CheckCircle, AlertCircle } from 'lucide-react';

const ContentOptimizationPanel = ({ keywords, pages, onRefresh }) => {
  const getKeywordDensity = (page, keyword) => {
    const content = `${page?.page_title} ${page?.meta_description} ${page?.h1_tag}`?.toLowerCase();
    const keywordLower = keyword?.keyword?.toLowerCase();
    const occurrences = (content?.match(new RegExp(keywordLower, 'g')) || [])?.length;
    return occurrences;
  };

  const getOptimizationScore = (page) => {
    let score = 0;
    if (page?.page_title && page?.page_title?.length >= 30 && page?.page_title?.length <= 60) score += 25;
    if (page?.meta_description && page?.meta_description?.length >= 120 && page?.meta_description?.length <= 160) score += 25;
    if (page?.h1_tag) score += 25;
    if (page?.target_keywords?.length > 0) score += 25;
    return score;
  };

  return (
    <div className="space-y-6">
      {pages?.map((page) => {
        let score = getOptimizationScore(page);
        const relevantKeywords = keywords?.filter(k =>
          page?.target_keywords?.includes(k?.keyword) ||
          getKeywordDensity(page, k) > 0
        );

        return (
          <div key={page?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{page?.page_title}</h3>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    score >= 75 ? 'bg-green-100 text-green-800' :
                    score >= 50 ? 'bg-yellow-100 text-yellow-800': 'bg-red-100 text-red-800'
                  }`}>
                    Score: {score}/100
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">{page?.page_url}</p>
              </div>
            </div>

            {/* Content Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Titre</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{page?.page_title?.length || 0} caractères</p>
                  {page?.page_title?.length >= 30 && page?.page_title?.length <= 60 ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <AlertCircle size={16} className="text-orange-600" />
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Meta Description</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{page?.meta_description?.length || 0} caractères</p>
                  {page?.meta_description?.length >= 120 && page?.meta_description?.length <= 160 ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <AlertCircle size={16} className="text-orange-600" />
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Balise H1</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{page?.h1_tag ? 'Présent' : 'Manquant'}</p>
                  {page?.h1_tag ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <AlertCircle size={16} className="text-red-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Keyword Mapping */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Mots-clés cibles</h4>
              <div className="flex flex-wrap gap-2">
                {page?.target_keywords?.map((keyword, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {page?.recommendations?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">Recommandations</h4>
                <ul className="space-y-1">
                  {page?.recommendations?.map((rec, idx) => (
                    <li key={idx} className="text-sm text-yellow-800 flex items-start">
                      <span className="mr-2">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}

      {pages?.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Aucune page à optimiser</p>
        </div>
      )}
    </div>
  );
};

export default ContentOptimizationPanel;